// Hook para gerenciar conexão WebSocket
import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '@/contexts/auth-hook';

const getSocketUrl = () => {
  const envUrl = import.meta.env.VITE_API_URL || '/api';
  // Remover /api do final se existir
  return envUrl.replace(/\/api$/, '');
};

export interface LockEvent {
  tipoRecurso: 'apreciacao' | 'maquina' | 'ativo';
  recursoId: string;
  usuarioId: string;
  usuarioNome?: string;
  action: 'locked' | 'unlocked' | 'updated';
  expiresAt?: string;
  timestamp: string;
}

export function useWebSocket() {
  const { isAuthenticated } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      // Desconectar se não estiver autenticado
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setConnected(false);
      }
      return;
    }

    // Conectar ao WebSocket.
    // A credencial é o cookie de sessão HttpOnly (RG-05) — o JavaScript não
    // consegue mais lê-la para pôr em `auth.token`. withCredentials faz o
    // handshake levar o cookie, e o servidor o lê de lá.
    const socket = io(getSocketUrl(), {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[WebSocket] Conectado');
      setConnected(true);
    });

    socket.on('disconnect', () => {
      console.log('[WebSocket] Desconectado');
      setConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('[WebSocket] Erro de conexão:', error);
      setConnected(false);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [isAuthenticated]);

  const joinResource = useCallback((tipoRecurso: string, recursoId: string) => {
    if (socketRef.current && connected) {
      socketRef.current.emit('join-resource', { tipoRecurso, recursoId });
    }
  }, [connected]);

  const leaveResource = useCallback((tipoRecurso: string, recursoId: string) => {
    if (socketRef.current && connected) {
      socketRef.current.emit('leave-resource', { tipoRecurso, recursoId });
    }
  }, [connected]);

  const sendHeartbeat = useCallback((tipoRecurso: string, recursoId: string) => {
    if (socketRef.current && connected) {
      socketRef.current.emit('heartbeat', { tipoRecurso, recursoId });
    }
  }, [connected]);

  const onLockEvent = useCallback((callback: (event: LockEvent) => void) => {
    if (!socketRef.current) return () => {};

    socketRef.current.on('lock-event', callback);
    socketRef.current.on('lock-updated', callback);

    return () => {
      if (socketRef.current) {
        socketRef.current.off('lock-event', callback);
        socketRef.current.off('lock-updated', callback);
      }
    };
  }, []);

  return {
    connected,
    joinResource,
    leaveResource,
    sendHeartbeat,
    onLockEvent
  };
}
